import * as RadixDialog from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Inbox,
  RotateCcw,
  X,
  type LucideIcon,
} from 'lucide-react';
import type {
  ButtonHTMLAttributes,
  SyntheticEvent,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';
import { createContext, useContext, useId, useState } from 'react';
import { Link } from 'react-router';

import { KeyFacts } from './fiche.js';
import { statusTone, type Tone } from './logic.js';
import { Panel } from './panel.js';

export { DataTable, Pagination, type Column } from './table.js';
export { BarChart, DonutChart, HBarChart, Meter, type Datum } from './charts.js';
export {
  formatDateTime,
  formatDay,
  formatMonth,
  formatPhone,
  formatVersion,
  phoneHref,
} from './format.js';
export {
  matchesQuery,
  sameValues,
  changedKeys,
  statusTone,
  readTrail,
  extendTrail,
  type Tone,
  type TrailStep,
} from './logic.js';
export {
  EntityLink,
  Related,
  type EntityCount,
  type RelatedGroup,
  type RelatedItem,
} from './entities.js';
export { ActionBar, ConfirmDialog, Drawer, Flash, ListHeader, useFlash } from './page.js';
export {
  Calendar,
  addDays,
  dayForKey,
  daysOfMonth,
  isoDay,
  leadingBlanks,
  mondayOf,
  parseIsoDay,
  shiftMonth,
  weekOf,
  weekdayIndex,
  type CalendarProps,
} from './calendar.js';
export { Panel } from './panel.js';
export {
  DrawerForm,
  FichePage,
  ListPage,
  StepDrawer,
  type DrawerFormProps,
  type FichePageProps,
  type ListPageProps,
  type StepDrawerProps,
} from './templates.js';
export {
  AsideCard,
  FicheLayout,
  FicheTabs,
  FilterPills,
  KeyFacts,
  MoreMenu,
  RelatedLinks,
  Repli,
  HumanData,
  humanKey,
  humanValue,
  statusLabel,
  type FicheTab,
} from './fiche.js';
export {
  ActionMenu,
  Breadcrumb,
  ChipFilter,
  FieldGrid,
  HelpTip,
  InlineEdit,
  Kbd,
  LinkCard,
  PageTabs,
  SaveBar,
  Segmented,
  SettingRow,
  SettingsSection,
  Switch,
  Tabs,
  useHotkey,
  useSettingsDraft,
  type ActionItem,
  type ChipOption,
  type Crumb,
  type PageTab,
  type TabItem,
} from './patterns.js';

/**
 * Composants de l'interface (research S10, charte docs/ux/charte.md) : habillage des
 * primitives accessibles et des classes `o-*` de la charte. Les écrans des modules
 * n'utilisent que ces composants.
 */

type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost';

export function Button({
  variant = 'default',
  small = false,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  small?: boolean;
}): ReactElement {
  const classes = ['o-button'];
  if (variant !== 'default') classes.push(`o-button--${variant}`);
  if (small) classes.push('o-button--small');
  if (className !== undefined) classes.push(className);
  return <button type="button" {...props} className={classes.join(' ')} />;
}

export function ButtonLink({
  to,
  children,
  variant = 'default',
  small = false,
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  small?: boolean;
}): ReactElement {
  const classes = ['o-button'];
  if (variant !== 'default') classes.push(`o-button--${variant}`);
  if (small) classes.push('o-button--small');
  return (
    <Link to={to} className={classes.join(' ')}>
      {children}
    </Link>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: (id: string, describedBy: string | undefined) => ReactNode;
}

/** Champ étiqueté : l'aide et l'erreur sont reliées au contrôle (lecteurs d'écran). */
export function Field({ label, hint, error, children }: FieldProps): ReactElement {
  const id = useId();
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  const errorId = error === undefined ? undefined : `${id}-error`;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="o-field">
      <label htmlFor={id}>{label}</label>
      {hint !== undefined && (
        <span id={hintId} className="o-hint">
          {hint}
        </span>
      )}
      {children(id, describedBy)}
      {error !== undefined && (
        <span id={errorId} className="o-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function TextInput({
  label,
  hint,
  error,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string | undefined;
}): ReactElement {
  return (
    <Field label={label} {...(hint === undefined ? {} : { hint })} error={error}>
      {(id, describedBy) => (
        <input
          id={id}
          className="o-input"
          aria-describedby={describedBy}
          aria-invalid={error !== undefined}
          {...props}
        />
      )}
    </Field>
  );
}

export function Select({
  label,
  hint,
  error,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  hint?: string;
  error?: string | undefined;
}): ReactElement {
  return (
    <Field label={label} {...(hint === undefined ? {} : { hint })} error={error}>
      {(id, describedBy) => (
        <select id={id} className="o-select" aria-describedby={describedBy} {...props}>
          {children}
        </select>
      )}
    </Field>
  );
}

/** Carte simple, sans titre = `Panel` sans titre (alias gardé pour les écrans existants). */
export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactElement {
  return (
    <Panel className={className === undefined ? 'o-card' : `o-card ${className}`}>{children}</Panel>
  );
}

/**
 * Contexte de page fourni par la coque : section de navigation de l'écran courant, affichée
 * au-dessus du titre (repère « où suis-je », comme le surtitre orange du WordPress).
 */
export const PageContext = createContext<{
  section?: string | undefined;
  /** La coque affiche déjà le fil d'Ariane : pas de surtitre par défaut. */
  trail?: boolean | undefined;
}>({});

export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  /** Surtitre ; par défaut la section de navigation de l'écran. */
  eyebrow?: string;
  /** Contenu sous le titre (indicateurs, onglets…). */
  children?: ReactNode;
}): ReactElement {
  const { section, trail } = useContext(PageContext);
  const over = eyebrow ?? (trail === true ? undefined : section);
  return (
    <header className="o-page-header">
      <div className="o-page-header__text">
        {over !== undefined && <p className="o-eyebrow">{over}</p>}
        <h1>{title}</h1>
        {description !== undefined && <p className="o-page-header__description">{description}</p>}
      </div>
      {actions !== undefined && <div className="o-page-header__actions">{actions}</div>}
      {children !== undefined && <div className="o-page-header__extra">{children}</div>}
    </header>
  );
}

export type Severity = 'ok' | 'warning' | 'critical';

/** Tuile d'accueil : un chiffre, un libellé, un lien. */
export function Tile({
  label,
  value,
  severity,
  link,
}: {
  label: string;
  value: ReactNode;
  severity?: Severity | undefined;
  link?: string | undefined;
}): ReactElement {
  return <StatCard label={label} value={value} tone={severity} link={link} />;
}

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: Tone | undefined;
  children: ReactNode;
}): ReactElement {
  return <span className={`o-badge o-badge--${tone}`}>{children}</span>;
}

/**
 * Badge de statut : pastille + libellé, ton déduit du code du statut (`statusTone`) sauf
 * s'il est donné ; la couleur n'est jamais la seule information.
 */
export function StatusBadge({
  status,
  label,
  tone,
}: {
  status: string | null | undefined;
  label?: string | undefined;
  tone?: Tone | undefined;
}): ReactElement {
  const t = tone ?? statusTone(status);
  return (
    <span className={`o-badge o-badge--${t} o-badge--dot`}>
      <span className="o-badge__dot" aria-hidden="true" />
      {label ?? status ?? '—'}
    </span>
  );
}

const URGENCY_LABEL = {
  vert: 'Dans les délais',
  orange: 'À surveiller',
  rouge: 'En retard',
} as const;

/** Indicateur d'urgence avec sa raison (constitution V : chaque proposition est expliquée). */
export function Urgency({
  level,
  reason,
}: {
  level: keyof typeof URGENCY_LABEL;
  reason?: string | undefined;
}): ReactElement {
  return (
    <span className="o-row">
      <span className={`o-badge o-badge--${level} o-badge--dot`}>
        <span className="o-badge__dot" aria-hidden="true" />
        {URGENCY_LABEL[level]}
      </span>
      {reason !== undefined && <span className="o-hint">{reason}</span>}
    </span>
  );
}

export function EmptyState({
  title,
  children,
  icon: Icon = Inbox,
  action,
}: {
  title: string;
  children?: ReactNode;
  icon?: LucideIcon;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="o-empty">
      <span className="o-empty__icon" aria-hidden="true">
        <Icon size={28} />
      </span>
      <p className="o-empty__title">
        <strong>{title}</strong>
      </p>
      {children}
      {action !== undefined && <div className="o-empty__action">{action}</div>}
    </div>
  );
}

export function ErrorMessage({ error }: { error: unknown }): ReactElement {
  const message = error instanceof Error ? error.message : 'Une erreur inattendue est survenue.';
  return (
    <div className="o-alert o-alert--error o-row" role="alert">
      <AlertTriangle aria-hidden="true" size={18} />
      <span>{message}</span>
    </div>
  );
}

export function Loading({ label = 'Chargement…' }: { label?: string }): ReactElement {
  return (
    <div className="o-row o-loading" role="status" aria-live="polite">
      <span className="o-spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/** Dialogue accessible (focus piégé, Échap, titre annoncé). */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  wide = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
}): ReactElement {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="o-overlay" />
        <RadixDialog.Content className={wide ? 'o-dialog o-dialog--wide' : 'o-dialog'}>
          <div className="o-dialog__header">
            <RadixDialog.Title asChild>
              <h2>{title}</h2>
            </RadixDialog.Title>
            <RadixDialog.Close asChild>
              <button
                type="button"
                className="o-button o-button--ghost o-icon-button"
                aria-label="Fermer"
              >
                <X aria-hidden="true" />
              </button>
            </RadixDialog.Close>
          </div>
          {description === undefined ? (
            <RadixDialog.Description className="o-visually-hidden">{title}</RadixDialog.Description>
          ) : (
            <RadixDialog.Description className="o-dialog__description">
              {description}
            </RadixDialog.Description>
          )}
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/**
 * Description de fiche (libellé / valeur) = `KeyFacts` (alias gardé pour les écrans
 * existants ; préférer `KeyFacts` dans le code neuf).
 */
export function Details({ items }: { items: [string, ReactNode][] }): ReactElement {
  return <KeyFacts items={items} />;
}

/* ------------------------------------------------------------------ */
/* Composants des tableaux de bord et des listes                       */
/* ------------------------------------------------------------------ */

/**
 * Carte d'indicateur : libellé, grand chiffre, pictogramme, détail ; liée à l'écran qui
 * détaille l'indicateur. Le ton colore le liseré et le pictogramme, jamais seul : le détail
 * dit pourquoi.
 */
export function StatCard({
  label,
  value,
  icon: Icon,
  tone,
  detail,
  link,
  children,
}: {
  label: string;
  value: ReactNode;
  icon?: LucideIcon | undefined;
  tone?: Tone | undefined;
  detail?: ReactNode;
  link?: string | undefined;
  children?: ReactNode;
}): ReactElement {
  const className = [
    'o-stat',
    tone === undefined ? '' : `o-stat--${tone}`,
    link === undefined ? '' : 'o-stat--link',
  ]
    .filter(Boolean)
    .join(' ');
  const body = (
    <>
      <span className="o-stat__head">
        <span className="o-stat__label">{label}</span>
        {Icon !== undefined && (
          <span className="o-stat__icon" aria-hidden="true">
            <Icon size={18} />
          </span>
        )}
      </span>
      <span
        className={
          typeof value === 'string' && value.length > 6
            ? 'o-stat__value o-stat__value--text'
            : 'o-stat__value'
        }
      >
        {value}
      </span>
      {detail !== undefined && <span className="o-stat__detail">{detail}</span>}
      {children}
    </>
  );
  return link === undefined ? (
    <div className={className}>{body}</div>
  ) : (
    <Link to={link} className={className}>
      {body}
    </Link>
  );
}

/** Rangée d'indicateurs (2 colonnes au téléphone, jusqu'à 5 sur grand écran). */
export function KpiGrid({
  children,
  label = 'Indicateurs',
  compact = false,
}: {
  children: ReactNode;
  label?: string;
  /** Cartes resserrées (nombreux indicateurs). */
  compact?: boolean;
}): ReactElement {
  return (
    <section className={compact ? 'o-kpis o-kpis--compact' : 'o-kpis'} aria-label={label}>
      {children}
    </section>
  );
}

/**
 * Barre de filtres au-dessus d'une liste : champs compacts sur une ligne (empilés au
 * téléphone), nombre de résultats et remise à zéro.
 */
export function FilterBar({
  children,
  count,
  onReset,
  label = 'Filtres',
}: {
  children: ReactNode;
  count?: number | undefined;
  onReset?: (() => void) | undefined;
  label?: string;
}): ReactElement {
  return (
    <div className="o-filterbar" role="search" aria-label={label}>
      <div className="o-filterbar__fields">{children}</div>
      {(count !== undefined || onReset !== undefined) && (
        <div className="o-filterbar__meta">
          {count !== undefined && (
            <span className="o-filterbar__count" aria-live="polite">
              {count} résultat{count > 1 ? 's' : ''}
            </span>
          )}
          {onReset !== undefined && (
            <button
              type="button"
              className="o-button o-button--ghost o-button--small"
              onClick={onReset}
            >
              <RotateCcw aria-hidden="true" size={16} /> Réinitialiser
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Tuile d'action (accueil bénévole) : pictogramme, libellé, indicateur facultatif. */
export function ActionTile({
  to,
  label,
  icon: Icon,
  hint,
  badge,
  tone,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  hint?: string | undefined;
  badge?: ReactNode;
  tone?: Tone | undefined;
}): ReactElement {
  return (
    <Link
      to={to}
      className={tone === undefined ? 'o-action-tile' : `o-action-tile o-action-tile--${tone}`}
    >
      <span className="o-action-tile__icon" aria-hidden="true">
        <Icon size={24} />
      </span>
      <span className="o-action-tile__text">
        <span className="o-action-tile__label">{label}</span>
        {hint !== undefined && <span className="o-action-tile__hint">{hint}</span>}
      </span>
      {badge !== undefined && badge !== null && (
        <span className="o-action-tile__badge">{badge}</span>
      )}
      <ChevronRight aria-hidden="true" size={18} className="o-action-tile__chevron" />
    </Link>
  );
}

export interface Step {
  title: string;
  content: ReactNode;
  /** Message bloquant le passage à l'étape suivante (champ manquant…). */
  invalid?: string | undefined;
}

/**
 * Formulaire en étapes : un seul `<form>`, les étapes masquées restent montées (aucune
 * saisie perdue), « Suivant » contrôle l'étape, la dernière soumet le formulaire.
 */
export function StepForm({
  steps,
  onSubmit,
  submitLabel,
  pending = false,
  footer,
  onCancel,
  id,
}: {
  steps: Step[];
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void;
  submitLabel: string;
  pending?: boolean;
  footer?: ReactNode;
  /** Bouton « Annuler » en tête des boutons (formulaire en étapes dans un tiroir). */
  onCancel?: (() => void) | undefined;
  id?: string | undefined;
}): ReactElement {
  const [current, setCurrent] = useState(0);
  const [blocked, setBlocked] = useState<string | undefined>();
  const last = current === steps.length - 1;
  const step = steps[current];
  const itemClass = (i: number): string =>
    i === current
      ? 'o-steps__item o-steps__item--current'
      : i < current
        ? 'o-steps__item o-steps__item--done'
        : 'o-steps__item';
  return (
    <form
      id={id}
      className="o-stepform"
      noValidate
      onSubmit={(e) => {
        // Contrôle natif des seuls champs de l'étape affichée (les autres sont masqués).
        const shown = e.currentTarget.querySelectorAll<HTMLFieldSetElement>(
          'fieldset.o-stepform__step',
        )[current];
        const fields = shown?.querySelectorAll<HTMLInputElement>('input, select, textarea');
        const invalid = [...(fields ?? [])].find((f) => !f.checkValidity());
        if (invalid !== undefined) {
          e.preventDefault();
          invalid.reportValidity();
          return;
        }
        if (!last) {
          e.preventDefault();
          if (step?.invalid !== undefined) {
            setBlocked(step.invalid);
            return;
          }
          setBlocked(undefined);
          setCurrent(current + 1);
          return;
        }
        onSubmit(e);
      }}
    >
      <ol className="o-steps" aria-label="Étapes">
        {steps.map((s, i) => (
          <li
            key={s.title}
            className={itemClass(i)}
            aria-current={i === current ? 'step' : undefined}
          >
            <span className="o-steps__number" aria-hidden="true">
              {i < current ? <Check size={14} /> : i + 1}
            </span>
            <span className="o-steps__title">{s.title}</span>
          </li>
        ))}
      </ol>
      {steps.map((s, i) => (
        <fieldset key={s.title} className="o-stepform__step" hidden={i !== current}>
          <legend className="o-visually-hidden">{s.title}</legend>
          {s.content}
        </fieldset>
      ))}
      {blocked !== undefined && (
        <p className="o-error" role="alert">
          {blocked}
        </p>
      )}
      {footer}
      <div className="o-stepform__actions">
        {onCancel !== undefined && (
          <button type="button" className="o-button o-stepform__cancel" onClick={onCancel}>
            Annuler
          </button>
        )}
        {current > 0 && (
          <button
            type="button"
            className="o-button"
            onClick={() => {
              setBlocked(undefined);
              setCurrent(current - 1);
            }}
          >
            Précédent
          </button>
        )}
        <button type="submit" className="o-button o-button--primary" disabled={last && pending}>
          {last ? submitLabel : 'Suivant'}
        </button>
      </div>
    </form>
  );
}
